/* =====================================================================
   Superette × Fish Wishlist v2 — save drawer clarity (v1.5, Wishlist V3)
   ⚠ JavaScript. Never paste into the Fish Wishlist Custom CSS box.
   The drawer that opens to pick a size (cards, recommenders, PDP with no size):
   · a status line at the top: "Choose your size to save it to Nicole's Wishlist" →
     "✓ Added to Nicole's Wishlist" once the size is picked (wishlist-enhancements.js saves it)
   · the default list sits on its own at the top; "Add to another list" heads the rest
   · "Your existing lists" heading and the Notify Me list are hidden here
   · desktop: once a size is chosen, drag the product onto any list to add it there (or tap +)
   · the messages follow what actually happened: "Added to / Removed from Nicole's Wishlist" at the top,
     "Added to / Removed from Wardrobe 01" under "Add to another list"
   · v1.4 (D2): if it's already in the default list the top line always says "Added to …" (also when
     the drawer is reopened before a size is picked); the grey line reads "Drag the item to move to another list." (desktop,
     worded like the Saved drawer) so it's clear it's about the other lists
   · v1.5 (V3): desktop, any single product image in a list can be dragged onto another list to move
     just that product there ("Moved to …"); the other images stay put
   · one-size products never show "Select your size"
   · tapping a list's name opens the lists drawer on that list (items showing)
   ===================================================================== */
(() => {
  const me = window.spWishlist || {};
  const first = String(me.firstName || '').trim();
  const fallbackName = first ? `${first}’s Wishlist` : 'Your Wishlist';
  const DEFAULT = 'WL1';
  const NOTIFY = 'WL10';

  const titleOf = (t) => { const n = t && t.querySelector('.wishlist-title'); return n ? n.textContent.trim() : ''; };

  const enhance = (drawer) => {
    const wrap = drawer.querySelector('.atw-drawer__wishlists-container');
    const box = drawer.querySelector('.atw-drawer-items__container');
    if (!wrap || !box) return;
    const tiles = [...box.querySelectorAll(':scope > atw-drawer-item')];
    const def = tiles.find((t) => t.dataset.id === DEFAULT) || tiles.find((t) => titleOf(t) === fallbackName) || null;
    tiles.forEach((t) => {
      t.classList.toggle('sp-atw-default', t === def);
      t.classList.toggle('sp-atw-notify', t.dataset.id === NOTIFY);
    });
    const others = tiles.filter((t) => t !== def && t.dataset.id !== NOTIFY);
    let label = box.querySelector(':scope > .sp-atw-other');
    let hint = box.querySelector(':scope > .sp-atw-hint');
    if (others.length) {
      if (!label) { label = document.createElement('p'); label.className = 'sp-atw-other'; label.textContent = 'Add to another list'; box.appendChild(label); }
      const hintText = canDrag() ? 'Drag the item to move to another list.' : 'Tap + Add to save it to another list too.';
      if (!hint) { hint = document.createElement('p'); hint.className = 'sp-atw-hint'; box.appendChild(hint); }
      if (hint.textContent !== hintText) hint.textContent = hintText;
    } else { if (label) label.remove(); if (hint) hint.remove(); }

    let status = wrap.querySelector(':scope > .sp-atw-status');
    if (!status) { status = document.createElement('div'); status.className = 'sp-atw-status'; status.setAttribute('role', 'status'); wrap.prepend(status); }
    const dd = drawer.querySelector('.sp-dd');
    const select = drawer.querySelector('wishlist-variant-selector select');
    // one-size products: never ask for a size — pick the only one (also when reopened via "Change")
    const ids = Array.isArray(drawer.variantIds) ? drawer.variantIds.map(String) : null;
    const opts = select ? [...select.options].filter((o) => o.value && (!ids || ids.includes(o.value))) : [];
    const oneSize = opts.length === 1;
    drawer.classList.toggle('sp-one-size', oneSize);
    if (oneSize && select.value !== opts[0].value) {
      try { if (typeof drawer.setCurrentVariantId === 'function') drawer.setCurrentVariantId(opts[0].value); } catch (e) { /* Fish not ready yet */ }
      select.value = opts[0].value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const sizeChosen = oneSize || (dd ? dd.classList.contains('has-value') : !!(select && select.value));
    const sizeKey = sizeChosen && select ? select.value : '';
    const listName = titleOf(def) || fallbackName;

    // what has the shopper done since this size was picked? (baseline resets per size / per opening)
    let mem = memory.get(drawer);
    if (!mem || mem.size !== sizeKey) {
      mem = { size: sizeKey, def: null, others: new Map(), last: null };
      memory.set(drawer, mem);
    }
    const defAdded = !!(def && def.dataset.isAdded === 'true');
    if (sizeChosen && mem.def === null) mem.def = defAdded ? 'was' : 'not';
    if (sizeChosen && defAdded && mem.def !== 'added' && mem.def !== 'was') mem.def = 'added';
    if (sizeChosen && !defAdded && (mem.def === 'added' || mem.def === 'was')) mem.def = 'removed';
    if (sizeChosen && defAdded && mem.def === 'removed') mem.def = 'added';
    // after a drag-move the top line says "Moved to …" until the shopper does something else
    const defId = def && def.dataset.id;
    if (mem.prevDef !== undefined && mem.prevDef !== defAdded) {
      const partOfMove = mem.moving && (mem.moving.from === defId || mem.moving.to === defId);
      if (!partOfMove) mem.movedTo = null;
      if (partOfMove && !defAdded && mem.moving.from === defId) mem.moving = null;
    }
    mem.prevDef = defAdded;

    let state;
    let text;
    if (mem.movedTo) { state = 'added'; text = `Moved to ${mem.movedTo}`; }
    else if (defAdded) { state = 'added'; text = `Added to ${listName}`; }
    else if (!sizeChosen) { state = 'size'; text = `Choose your size to save it to ${listName}`; }
    else if (mem.def === 'removed') { state = 'removed'; text = `Removed from ${listName}`; }
    else if (drawer.dataset.spSaving === 'true') { state = 'ready'; text = `Saving to ${listName}…`; }
    else { state = 'ready'; text = `Tap + to save it to ${listName}`; }
    if (status.dataset.state !== state) status.dataset.state = state;
    if (status.textContent !== text) status.textContent = text;

    // other lists: a line under "Add to another list" for the latest add / remove
    others.forEach((t) => {
      const added = t.dataset.isAdded === 'true';
      const before = mem.others.get(t.dataset.id);
      if (sizeChosen && before !== undefined && before !== added) {
        if (mem.moving && t.dataset.id === mem.moving.from) { if (!added) mem.moving = null; } // the old half of a move: say "Moved to …", not "Removed from …"
        else {
          const moved = !!(added && mem.moving && mem.moving.to === t.dataset.id);
          if (!moved) mem.movedTo = null;
          mem.last = { name: titleOf(t), added, moved };
        }
      }
      mem.others.set(t.dataset.id, added);
    });
    let otherStatus = box.querySelector(':scope > .sp-atw-status--other');
    if (mem.last && others.length) {
      if (!otherStatus) { otherStatus = document.createElement('div'); otherStatus.className = 'sp-atw-status sp-atw-status--other'; otherStatus.setAttribute('role', 'status'); }
      if (hint && otherStatus.previousElementSibling !== hint) hint.after(otherStatus);
      const st = mem.last.added ? 'added' : 'removed';
      const tx = `${mem.last.moved ? 'Moved to' : mem.last.added ? 'Added to' : 'Removed from'} ${mem.last.name}`;
      if (otherStatus.dataset.state !== st) otherStatus.dataset.state = st;
      if (otherStatus.textContent !== tx) otherStatus.textContent = tx;
    } else if (otherStatus) otherStatus.remove();
    enableDrag(drawer, sizeChosen, [def, ...others].filter(Boolean));
  };

  const memory = new WeakMap();

  /* ---------- desktop: drag the product onto a list ---------- */
  const canDrag = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const addTo = (tile) => {
    if (!tile || tile.dataset.isAdded === 'true') return;
    const add = tile.querySelector('.atw-drawer__item-cta .fw2-btn--solid');
    if (add) add.click();
    tile.classList.add('sp-just-saved');
    setTimeout(() => tile.classList.remove('sp-just-saved'), 1600);
  };
  // move one product (by variant id) from one list to another with Fish's own add / remove, then redraw the lists
  const clean = (v) => String(v || '').replace(/[^\d]/g, '');
  const listItems = (id) => { const w = window.FishWishlist && window.FishWishlist.wishlists; return (w && Array.isArray(w[id])) ? w[id] : []; };
  const moveTo = (drawer, vid, from, to) => {
    const F = window.FishWishlist;
    if (!F || !vid || !from || !to || from === to) return;
    const fromId = from.dataset.id;
    const toId = to.dataset.id;
    const item = listItems(fromId).find((x) => clean(x && x.id) === clean(vid));
    if (!item) return;
    const mem = memory.get(drawer);
    if (mem) mem.moving = { from: fromId, to: toId };
    try {
      if (!listItems(toId).some((x) => clean(x && x.id) === clean(vid))) F.addToWishlist(Object.assign({}, item), toId);
      F.removeFromWishlist(item.id, fromId);
    } catch (e) { if (mem) mem.moving = null; return; }
    if (mem) { mem.last = { name: titleOf(to), added: true, moved: true }; mem.movedTo = titleOf(to); }
    if (typeof drawer.renderWishlistItems === 'function') drawer.renderWishlistItems();
    const target = drawer.querySelector(`atw-drawer-item[data-id="${toId}"]`);
    if (target) { target.classList.add('sp-just-saved'); setTimeout(() => target.classList.remove('sp-just-saved'), 1600); }
    setTimeout(() => { if (mem) mem.moving = null; enhance(drawer); }, 400);
    enhance(drawer);
  };
  const endDrag = (drawer) => {
    delete drawer.dataset.spDragging;
    drawer.spDragFrom = null;
    drawer.spDragVid = null;
    drawer.querySelectorAll('.sp-drop-over, .sp-drag-from').forEach((t) => t.classList.remove('sp-drop-over', 'sp-drag-from'));
  };
  const enableDrag = (drawer, sizeChosen, tiles) => {
    const card = drawer.querySelector('.wishlist-variant-selector__card');
    if (!card) return;
    const on = canDrag() && sizeChosen;
    if (card.draggable !== on) card.draggable = on;
    card.classList.toggle('sp-atw-draggable', on);
    if (!card.dataset.spDrag) {
      card.dataset.spDrag = 'true';
      card.addEventListener('dragstart', (e) => {
        if (!card.draggable) { e.preventDefault(); return; }
        drawer.dataset.spDragging = 'true';
        drawer.spDragFrom = null;
        try { e.dataTransfer.effectAllowed = 'copy'; e.dataTransfer.setData('text/plain', 'sp-wishlist-item'); } catch (x) { /* older browsers */ }
      });
      card.addEventListener('dragend', () => endDrag(drawer));
    }
    // each product image in a list: drag that one product onto another list to move it
    tiles.forEach((tile) => {
      const row = tile.querySelector('.wishlist-item__row');
      if (row && row.draggable) row.draggable = false;
      const items = listItems(tile.dataset.id);
      tile.querySelectorAll('.wishlist-item__placeholder').forEach((thumb, i) => {
        const img = thumb.querySelector('img');
        const item = img && items[i];
        const can = canDrag() && !!item;
        if (thumb.draggable !== can) thumb.draggable = can;
        thumb.classList.toggle('sp-atw-draggable', can);
        if (item) thumb.dataset.spVid = clean(item.id); else delete thumb.dataset.spVid;
        if (img && img.getAttribute('draggable') !== 'false') img.setAttribute('draggable', 'false');
        if (thumb.dataset.spDrag) return;
        thumb.dataset.spDrag = 'true';
        thumb.addEventListener('dragstart', (e) => {
          e.stopPropagation();
          if (!thumb.draggable || !thumb.dataset.spVid) { e.preventDefault(); return; }
          drawer.dataset.spDragging = 'true';
          drawer.spDragFrom = thumb.closest('atw-drawer-item');
          drawer.spDragVid = thumb.dataset.spVid;
          thumb.classList.add('sp-drag-from');
          try {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', 'sp-wishlist-item');
            const pic = thumb.querySelector('img');
            if (pic) e.dataTransfer.setDragImage(pic, pic.offsetWidth / 2, pic.offsetHeight / 2);
          } catch (x) { /* older browsers */ }
        });
        thumb.addEventListener('dragend', () => endDrag(drawer));
      });
    });
    tiles.forEach((tile) => {
      if (tile.dataset.spDrop) return;
      tile.dataset.spDrop = 'true';
      const ok = () => drawer.dataset.spDragging === 'true';
      tile.addEventListener('dragover', (e) => {
        if (!ok() || drawer.spDragFrom === tile) return;
        e.preventDefault();
        try { e.dataTransfer.dropEffect = drawer.spDragFrom ? 'move' : 'copy'; } catch (x) { /* */ }
        tile.classList.add('sp-drop-over');
      });
      tile.addEventListener('dragleave', (e) => { if (!tile.contains(e.relatedTarget)) tile.classList.remove('sp-drop-over'); });
      tile.addEventListener('drop', (e) => {
        if (!ok() || drawer.spDragFrom === tile) return;
        e.preventDefault();
        const from = drawer.spDragFrom;
        const vid = drawer.spDragVid;
        endDrag(drawer);
        if (from) moveTo(drawer, vid, from, tile); else addTo(tile);
      });
    });
  };

  /* ---------- a list's name → open that list in the lists drawer ---------- */
  document.addEventListener('click', (e) => {
    const name = e.target.closest && e.target.closest('atw-drawer .atw-drawer__item-name, atw-drawer .wishlist-title');
    if (!name) return;
    const tile = name.closest('atw-drawer-item');
    const atw = name.closest('atw-drawer');
    const lists = document.querySelector('fw2-drawer');
    if (!tile || !tile.dataset.id || !lists || typeof lists.open !== 'function') return;
    e.preventDefault();
    e.stopPropagation();
    const id = tile.dataset.id;
    if (typeof atw.close === 'function') atw.close();
    setTimeout(() => {
      lists.open();
      let tries = 0;
      const show = () => {
        const card = lists.querySelector(`fw2-list-card[data-id="${id}"]`);
        if (!card) { if (tries++ < 20) setTimeout(show, 100); return; }
        if (typeof lists.showList === 'function') lists.showList(id);
        else if (typeof card.showItemCards === 'function') card.showItemCards();
        setTimeout(() => card.scrollIntoView({ block: 'start', behavior: 'smooth' }), 80);
      };
      show();
    }, 250);
  }, true);

  const watch = () => {
    document.querySelectorAll('atw-drawer').forEach((drawer) => {
      if (drawer.dataset.spClarity) return;
      drawer.dataset.spClarity = 'true';
      let queued = false;
      const schedule = () => { if (queued) return; queued = true; setTimeout(() => { queued = false; enhance(drawer); }, 40); };
      new MutationObserver((muts) => {
        if (muts.every((m) => m.target.classList && m.target.classList.contains('sp-atw-status'))) return;
        schedule();
      }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-is-added', 'data-open', 'class'] });
      drawer.addEventListener('change', schedule);
      new MutationObserver(() => { if (drawer.dataset.open !== 'true') memory.delete(drawer); })
        .observe(drawer, { attributes: true, attributeFilter: ['data-open'] });
      enhance(drawer);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch); else watch();
  window.addEventListener('load', watch);
})();
